import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

const PhotoCropper = ({ imageSrc, onComplete, onCancel, initialCropState = null }) => {
  // Initialize with saved state or defaults
  const [crop, setCrop] = useState(initialCropState?.crop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialCropState?.zoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Display dimensions: 1080x870 pixels (what user sees)
  // Output dimensions: 1080x970 pixels (what admin gets)
  const DISPLAY_WIDTH = 1080;
  const DISPLAY_HEIGHT = 1070;
  const DISPLAY_ASPECT_RATIO = DISPLAY_WIDTH / DISPLAY_HEIGHT; // 1.24
  
  const OUTPUT_WIDTH = 1080;
  const OUTPUT_HEIGHT = 1075; // Taller output for admin
  const OUTPUT_ASPECT_RATIO = OUTPUT_WIDTH / OUTPUT_HEIGHT; // 1.11

  // Save crop state when it changes
  useEffect(() => {
    if (initialCropState) {
      // Update the parent's saved state
      initialCropState.crop = crop;
      initialCropState.zoom = zoom;
    }
  }, [crop, zoom, initialCropState]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, croppedAreaPixels) => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size to OUTPUT dimensions - 1080x970 pixels
      canvas.width = OUTPUT_WIDTH;
      canvas.height = OUTPUT_HEIGHT;

      // Calculate the output crop area based on display crop
      // The output crop is slightly taller than what the user sees
      const outputCropX = croppedAreaPixels.x;
      const outputCropY = croppedAreaPixels.y;
      const outputCropWidth = croppedAreaPixels.width;
      const outputCropHeight = croppedAreaPixels.height * (OUTPUT_HEIGHT / DISPLAY_HEIGHT);

      // Draw the cropped image at output dimensions without any zoom scaling
      ctx.drawImage(
        image,
        outputCropX,
        outputCropY,
        outputCropWidth,
        outputCropHeight,
        0,
        0,
        OUTPUT_WIDTH,
        OUTPUT_HEIGHT
      );

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          // Create both blob and URL for flexibility
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        }, 'image/jpeg', 0.9);
      });
    } catch (error) {
      console.error('Error creating cropped image:', error);
      throw error;
    }
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;

    setIsLoading(true);
    try {
      const { blob, url } = await getCroppedImg(imageSrc, croppedAreaPixels);
      onComplete(blob, {
        crop,
        zoom,
        croppedAreaPixels,
        output: { width: OUTPUT_WIDTH, height: OUTPUT_HEIGHT }
      });
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleZoomChange = (e) => {
    const newZoom = parseFloat(e.target.value);
    // Ensure zoom is between 1.0 and 3.0 (no zooming out)
    const clampedZoom = Math.max(1.0, Math.min(3.0, newZoom));
    setZoom(clampedZoom);
  };

  const resetCrop = () => {
    // Reset to initial state where full image fits in crop box
    setCrop({ x: 0, y: 0 });
    setZoom(1.0);
  };

  return (
    <div className="photo-cropper-modal">
      <div className="cropper-content">
        <div className="cropper-body">
          <div className="crop-container">
            <div className="crop-wrapper">
                             <Cropper
                 image={imageSrc}
                 crop={crop}
                 zoom={zoom}
                 aspect={DISPLAY_ASPECT_RATIO}
                 onCropChange={setCrop}
                 onZoomChange={setZoom}
                 onCropComplete={onCropComplete}
                 showGrid={true}
                 objectFit="contain"
                 style={{
                   containerStyle: {
                     width: '100%',
                     height: '350px', // Reduced height to fit buttons
                     backgroundColor: '#000',
                     position: 'relative'
                   }
                 }}
               />
              
              {/* Fixed crop highlight overlay - always centered */}
              <div className="fixed-crop-highlight">
                <div className="crop-window">
                  <div className="crop-window-content">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="zoom-controls">
            <label htmlFor="zoom-slider">Zoom: {zoom.toFixed(1)}x</label>
            <input
              id="zoom-slider"
              type="range"
              min="1.0"
              max="3.0"
              step="0.1"
              value={zoom}
              onChange={handleZoomChange}
              className="zoom-slider"
            />
            <span className="zoom-range">1.0x - 3.0x</span>
          </div>

          <div className="cropper-controls">
            <button onClick={resetCrop} className="control-btn reset">
              ↻ Reset
            </button>
            <button onClick={onCancel} className="control-btn cancel">
              ✕ Cancel
            </button>
            <button 
              onClick={handleConfirm} 
              disabled={!croppedAreaPixels || isLoading}
              className="control-btn confirm"
            >
              {isLoading ? 'Processing...' : '✓ Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoCropper;
